import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../Outbox/datepicker-overrides.css";
import moment from "moment";
import Swal from "sweetalert2";
import { sendBulkSMS, updateSent } from "store/bulk-lists/actions";
import {
  getScheduledList,
  getScheduledDetail,
  cancelScheduled,
  freezeScheduled,
  resetScheduledFlag,
} from "store/scheduled/actions";
import { getContactLists } from "store/contacts-lists/actions";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
} from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";

// MessageStatus values the messaging service can return on a scheduled_sms row.
const STATUS_OPTIONS = [
  "PENDING",
  "QUEUED",
  "SENT",
  "DELIVERED",
  "FAILED",
  "REJECTED",
  "CANCELLED",
  "CANCELLED_LIST_DELETED",
];

const statusColor = (status) => {
  switch (status) {
    case "PENDING":
      return "warning";
    case "QUEUED":
      return "info";
    case "SENT":
      return "primary";
    case "DELIVERED":
      return "success";
    case "FAILED":
    case "REJECTED":
      return "danger";
    default:
      return "secondary";
  }
};

const ScheduledMessages = () => {
  const dispatch = useDispatch();
  const [statusFilter, setStatusFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState({ pageSize: 10, page: 0 });
  const [bulkSmsModal, setBulkSmsModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [bulkSmsData, setBulkSmsData] = useState({
    sender: "",
    send_date_time: "",
    contactList: [],
    message: "",
    characterCount: 0,
  });

  document.title = "Scheduled Messages | Outbox";

  const fetchScheduled = () => {
    dispatch(
      getScheduledList({
        page: pagination.page,
        pageSize: pagination.pageSize,
        status: statusFilter || undefined,
      })
    );
  };

  useEffect(() => {
    fetchScheduled();
  }, [pagination, statusFilter, dispatch]);

  // Load the full list catalog once so the picker + name lookup work regardless
  // of the scheduled-table pagination (mirrors the Outbox page).
  useEffect(() => {
    dispatch(getContactLists({ page: 0, pageSize: 200 }));
  }, [dispatch]);

  const CombinedProperties = createSelector(
    (state) => state.Scheduled,
    (state) => state.ContactList,
    (state) => state.Config,
    (state) => state.User,
    (state) => state.BulkList,
    (scheduled, contactList, config, user, bulkList) => ({
      scheduledList: scheduled.scheduledList,
      scheduledTotal: scheduled.scheduledTotal,
      scheduledTotalPages: scheduled.scheduledTotalPages,
      scheduledDetail: scheduled.scheduledDetail,
      detailLoading: scheduled.detailLoading,
      cancelled: scheduled.cancelled,
      frozen: scheduled.frozen,
      contactLists: contactList.contactLists,
      shortcodes: config.shortcodes,
      userProfile: user.userProfile,
      bulkSent: bulkList.bulkSent,
    })
  );

  const {
    scheduledList,
    scheduledTotal,
    scheduledTotalPages,
    scheduledDetail,
    detailLoading,
    cancelled,
    frozen,
    contactLists,
    shortcodes,
    userProfile,
    bulkSent,
  } = useSelector(CombinedProperties);

  const listName = (id) => {
    const l = (contactLists || []).find((e) => e.id === id);
    return l ? l.name : `#${id}`;
  };

  // Client-side text filter over the loaded page (no server keyword search).
  const filtered = (scheduledList || []).filter((r) => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.message || "").toLowerCase().includes(q) ||
      (r.shortcode || "").toLowerCase().includes(q)
    );
  });

  const showNotification = (title, message, icon) =>
    Swal.fire({ icon, title, text: message, confirmButtonText: "OK" });

  // Refresh + close the create modal once a schedule was created.
  useEffect(() => {
    if (bulkSent) {
      setBulkSmsModal(false);
      dispatch(updateSent());
      fetchScheduled();
      showNotification("Success", "Bulk Message scheduled successfully", "success");
    }
  }, [bulkSent]);

  // Refresh once a cancellation lands.
  useEffect(() => {
    if (cancelled) {
      dispatch(resetScheduledFlag());
      setViewModal(false);
      fetchScheduled();
      showNotification("Cancelled", "Scheduled message cancelled", "success");
    }
  }, [cancelled]);

  // Once a freeze lands: refresh the list + the open detail, and confirm.
  useEffect(() => {
    if (frozen) {
      dispatch(resetScheduledFlag());
      fetchScheduled();
      if (viewRow) dispatch(getScheduledDetail(viewRow.id));
      showNotification(
        "Recipients frozen",
        "The recipient list is now locked as of this moment.",
        "success"
      );
    }
  }, [frozen]);

  const handleFreeze = (row) => {
    Swal.fire({
      icon: "question",
      title: "Freeze recipients now?",
      text: "The recipient list will be locked to its current membership. Later changes to the contact list will no longer be picked up when this send fires.",
      showCancelButton: true,
      confirmButtonText: "Freeze now",
      cancelButtonText: "Keep live",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(freezeScheduled(row.id));
      }
    });
  };

  const handleCancel = (row) => {
    Swal.fire({
      icon: "warning",
      title: "Cancel this scheduled message?",
      text: "This cannot be undone. The message will not be sent.",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it",
      cancelButtonText: "Keep it",
      confirmButtonColor: "#dc3545",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(cancelScheduled(row.id));
      }
    });
  };

  const onChangePagination = ({ page, limit }) => {
    setPagination({
      ...pagination,
      page: page ?? pagination.page,
      pageSize: limit ?? pagination.pageSize,
    });
  };

  // ─── Create (reuses the messaging scheduled-send path via sendBulkSMS) ───
  const toggleBulkSmsModal = () => setBulkSmsModal(!bulkSmsModal);

  const handleBulkSmsChange = (e) => {
    const { name, value } = e.target;
    setBulkSmsData((prev) => ({
      ...prev,
      [name]: value,
      characterCount: name === "message" ? value.length : prev.characterCount,
    }));
  };

  const handleBulkDateChange = (date) => {
    setBulkSmsData((prev) => ({ ...prev, send_date_time: date }));
  };

  const handleFieldInsert = (field) => {
    setBulkSmsData((prev) => {
      const message = `${prev.message} ${field}`.trim();
      return { ...prev, message, characterCount: message.length };
    });
  };

  const handleScheduleBulk = () => {
    if (!bulkSmsData.send_date_time) {
      showNotification("Missing date", "Pick a send date & time", "error");
      return;
    }
    if (!moment(bulkSmsData.send_date_time).isAfter(moment().add(1, "minute"))) {
      showNotification("Invalid time", "Scheduled time must be in the future", "error");
      return;
    }
    if (!bulkSmsData.contactList || bulkSmsData.contactList.length === 0) {
      showNotification("Missing list", "Select at least one contact list", "error");
      return;
    }
    if (!bulkSmsData.message.trim()) {
      showNotification("Missing message", "Enter a message", "error");
      return;
    }
    const params = {
      short_code: bulkSmsData.sender || shortcodes?.[0]?.shortcode,
      gateway: 1,
      send_date_time: moment(bulkSmsData.send_date_time).format("YYYY-MM-DD HH:mm"),
      message: bulkSmsData.message,
      status: 1,
      type: 1,
      subject: bulkSmsData.subject || " ",
    };
    dispatch(
      sendBulkSMS({
        params,
        contact_list_id: bulkSmsData.contactList,
      })
    );
  };

  const openView = (row) => {
    setViewRow(row);
    setViewModal(true);
    dispatch(getScheduledDetail(row.id));
  };

  const columns = useMemo(
    () => [
      { header: "From", accessorKey: "shortcode", enableColumnFilter: false, enableSorting: true },
      {
        header: "Message",
        accessorKey: "message",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (info) => <div style={messageColumnStyle}>{info.getValue()}</div>,
      },
      {
        header: "Scheduled For",
        accessorKey: "send_date_time",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }) => (getValue() ? moment(getValue()).format("DD MMM YYYY hh:mm A") : "—"),
      },
      {
        header: "Recipients",
        accessorKey: "send_count",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const v = info.getValue();
          return v == null ? "—" : Number(v).toLocaleString();
        },
      },
      {
        header: "Lists",
        accessorKey: "listIds",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const ids = info.getValue() || [];
          if (!ids.length) return "—";
          return <div style={messageColumnStyle}>{ids.map(listName).join(", ")}</div>;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => (
          <Badge color={statusColor(info.getValue())} className="font-size-12">
            {info.getValue()}
          </Badge>
        ),
      },
      {
        header: "Actions",
        cell: (cellProps) => {
          const row = cellProps.row.original;
          return (
            <div className="d-flex gap-3">
              <Link
                to="#"
                className="text-primary"
                onClick={(e) => {
                  e.preventDefault();
                  openView(row);
                }}
                title="View"
              >
                <i className="mdi mdi-eye font-size-18" />
              </Link>
              {row.status === "PENDING" && (
                <Link
                  to="#"
                  className="text-danger"
                  onClick={(e) => {
                    e.preventDefault();
                    handleCancel(row);
                  }}
                  title="Cancel"
                >
                  <i className="mdi mdi-close-circle-outline font-size-18" />
                </Link>
              )}
            </div>
          );
        },
      },
    ],
    [contactLists]
  );

  const reports = [
    {
      title: "Total Units Used",
      iconClass: "bx-envelope",
      description: (userProfile?.Client?.total_used_units || 0).toLocaleString(),
    },
    {
      title: "Units Remaining",
      iconClass: "bx-bar-chart-alt-2",
      description: (userProfile?.Client?.remaining_units || 0).toLocaleString(),
    },
  ];

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Sent Messages" breadcrumbItem="Scheduled Messages" />

        <Row>
          {reports.map((report, key) => (
            <Col md="6" key={"_col_" + key}>
              <Card className="mini-stats-wid">
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-grow-1">
                      <p className="text-muted fw-medium">{report.title}</p>
                      <h4 className="mb-0">{report.description}</h4>
                    </div>
                    <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                      <span className="avatar-title rounded-circle bg-primary">
                        <i className={"bx " + report.iconClass + " font-size-24"}></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 mb-3">
          <div className="d-flex align-items-end gap-2">
            <div className="me-3">
              <Label className="mb-1">Search</Label>
              <Input
                style={{ minWidth: 320 }}
                placeholder="Search messages…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1">Status</Label>
              <Input
                type="select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination((p) => ({ ...p, page: 0 }));
                }}
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Input>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button color="primary" onClick={toggleBulkSmsModal}>
              <i className="mdi mdi-plus"></i> Schedule Bulk SMS
            </Button>
          </div>
        </div>

        <TableContainer
          columns={columns}
          data={filtered}
          isGlobalFilter={false}
          isPagination={true}
          isCustomPageSize={true}
          pageSizeAtBottom={true}
          paginationData={pagination}
          onChangePagination={onChangePagination}
          manualPagination={true}
          pageCount={scheduledTotalPages}
          totalRows={scheduledTotal}
          onRowClick={(row) => openView(row)}
          SearchPlaceholder="Search messages..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        {/* View details modal */}
        <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
          <ModalHeader toggle={() => setViewModal(false)}>Scheduled Message</ModalHeader>
          <ModalBody className="p-4">
            {viewRow && (
              <div className="d-flex flex-column gap-3">
                <div>
                  <Label className="fw-semibold mb-1">Status</Label>
                  <div>
                    <Badge color={statusColor(viewRow.status)} className="font-size-12">
                      {viewRow.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="fw-semibold mb-1">From</Label>
                  <div>{viewRow.shortcode}</div>
                </div>
                <div>
                  <Label className="fw-semibold mb-1">Scheduled For</Label>
                  <div>
                    {viewRow.send_date_time
                      ? moment(viewRow.send_date_time).format("DD MMM YYYY hh:mm A")
                      : "—"}
                  </div>
                </div>
                <div>
                  <Label className="fw-semibold mb-1">Recipient Resolution</Label>
                  <div>
                    {scheduledDetail?.recipientResolution || viewRow.recipientResolution || "—"}
                    {scheduledDetail?.resolvedAt && (
                      <span className="text-muted">
                        {" "}
                        (resolved {moment(scheduledDetail.resolvedAt).format("DD MMM YYYY hh:mm A")})
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="fw-semibold mb-1">Lists</Label>
                  <div>
                    {viewRow.listIds && viewRow.listIds.length
                      ? viewRow.listIds.map(listName).join(", ")
                      : "—"}
                  </div>
                </div>
                <div>
                  <Label className="fw-semibold mb-1">Recipients</Label>
                  <div>
                    {(() => {
                      const count = scheduledDetail?.resolvedCount ?? viewRow.send_count;
                      return count == null ? "—" : Number(count).toLocaleString();
                    })()}
                  </div>
                </div>

                {/* List-change (drift) summary — only present for LIVE schedules
                    that have observed contact-list changes since scheduling. */}
                {detailLoading ? (
                  <div className="text-muted">Loading list-change summary…</div>
                ) : (
                  scheduledDetail?.driftSummary && (
                    <div className="border rounded p-3 bg-light">
                      <Label className="fw-semibold mb-2 d-block">
                        <i className="mdi mdi-account-switch me-1" />
                        List changes since scheduling
                      </Label>
                      <div className="d-flex flex-wrap gap-3 mb-2">
                        <span className="text-success">
                          +{Number(scheduledDetail.driftSummary.totalAdded || 0).toLocaleString()} added
                        </span>
                        <span className="text-danger">
                          −{Number(scheduledDetail.driftSummary.totalRemoved || 0).toLocaleString()} removed
                        </span>
                        {scheduledDetail.driftSummary.anyListDeleted && (
                          <span className="text-danger fw-semibold">a list was deleted</span>
                        )}
                        {scheduledDetail.driftSummary.lastChangedAt && (
                          <span className="text-muted">
                            last change{" "}
                            {moment(scheduledDetail.driftSummary.lastChangedAt).format(
                              "DD MMM YYYY hh:mm A"
                            )}
                          </span>
                        )}
                      </div>
                      {Array.isArray(scheduledDetail.driftSummary.perList) &&
                        scheduledDetail.driftSummary.perList.length > 0 && (
                          <ul className="mb-0 ps-3">
                            {scheduledDetail.driftSummary.perList.map((d) => (
                              <li key={d.listId}>
                                {listName(d.listId)}: +{d.addedCount || 0} / −{d.removedCount || 0}
                                {d.listDeleted && <span className="text-danger"> (deleted)</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  )
                )}

                <div>
                  <Label className="fw-semibold mb-1">Message</Label>
                  <div style={{ whiteSpace: "pre-wrap" }}>{viewRow.message}</div>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter className="px-4 pb-4">
            {viewRow?.status === "PENDING" &&
              (scheduledDetail?.recipientResolution || viewRow.recipientResolution) === "LIVE" && (
                <Button color="info" outline onClick={() => handleFreeze(viewRow)}>
                  Freeze Recipients
                </Button>
              )}
            {viewRow?.status === "PENDING" && (
              <Button
                color="danger"
                outline
                onClick={() => {
                  setViewModal(false);
                  handleCancel(viewRow);
                }}
              >
                Cancel Schedule
              </Button>
            )}
            <Button color="secondary" outline onClick={() => setViewModal(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* Schedule Bulk SMS modal — reuses the messaging sms/scheduled create path */}
        <Modal isOpen={bulkSmsModal} toggle={toggleBulkSmsModal} size="lg">
          <ModalHeader toggle={toggleBulkSmsModal}>Schedule a Bulk Message</ModalHeader>
          <ModalBody className="p-4">
            <div className="mb-4">
              <Label for="sender" className="fw-semibold mb-2">* SEND FROM</Label>
              <Input
                type="select"
                name="sender"
                id="sender"
                defaultValue={shortcodes?.[0]?.shortcode}
                value={bulkSmsData.sender}
                onChange={handleBulkSmsChange}
              >
                {shortcodes?.map((e) => (
                  <option key={e.shortcode} value={e.shortcode}>
                    {e.shortcode}
                  </option>
                ))}
              </Input>
            </div>

            <div className="mb-4">
              <Label for="date" className="fw-semibold mb-2">* Send Date &amp; Time</Label>
              <DatePicker
                id="send_date_time"
                selected={bulkSmsData.send_date_time ? new Date(bulkSmsData.send_date_time) : null}
                onChange={handleBulkDateChange}
                showTimeSelect
                timeIntervals={5}
                timeCaption="Time"
                minDate={new Date()}
                dateFormat="EEE, dd MMM yyyy h:mm aa"
                placeholderText="Select date & time"
                autoComplete="off"
                className="form-control"
                wrapperClassName="kengen-dtp-wrapper"
                popperClassName="kengen-dtp-popper"
                popperPlacement="bottom-start"
              />
            </div>

            <div className="mb-4">
              <Label for="contactList" className="fw-semibold mb-2">* Contact List</Label>
              <Select
                isMulti
                name="contactList"
                id="contactList"
                options={contactLists.map((e) => ({ value: e.id, label: e.name }))}
                value={bulkSmsData.contactList
                  .map((id) => {
                    const contact = contactLists.find((e) => e.id === id);
                    return contact ? { value: id, label: contact.name } : null;
                  })
                  .filter(Boolean)}
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions.map((option) => option.value);
                  setBulkSmsData((prev) => ({ ...prev, contactList: selectedValues }));
                }}
                placeholder="Select Contact List"
                styles={{
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#6c757d" : "#f8f9fa",
                    color: state.isSelected ? "#fff" : "#212529",
                    ":hover": { backgroundColor: "#4E3080", color: "#fff" },
                  }),
                  multiValue: (provided) => ({ ...provided, backgroundColor: "#6c757d", color: "#fff" }),
                  multiValueLabel: (provided) => ({ ...provided, color: "#fff" }),
                  multiValueRemove: (provided) => ({
                    ...provided,
                    color: "#fff",
                    ":hover": { backgroundColor: "#5a6268", color: "#fff" },
                  }),
                }}
              />
            </div>

            <div className="mb-3">
              <Label className="fw-semibold mb-2">Salutation</Label>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <Button size="sm" color="info" onClick={() => handleFieldInsert("<FIRSTNAME>")}>
                  &lt;FIRSTNAME&gt;
                </Button>
                <Button size="sm" color="info" onClick={() => handleFieldInsert("<LASTNAME>")}>
                  &lt;LASTNAME&gt;
                </Button>
                <Button size="sm" color="info" onClick={() => handleFieldInsert("<DATE>")}>
                  &lt;DATE&gt;
                </Button>
              </div>
              <Input
                type="textarea"
                name="message"
                placeholder="Enter message"
                value={bulkSmsData.message}
                onChange={handleBulkSmsChange}
                rows={5}
              />
            </div>

            <div className="d-flex justify-content-between align-items-center text-muted">
              <span>Characters: {bulkSmsData.characterCount}/160</span>
              <span>Messages: {Math.ceil(bulkSmsData.characterCount / 160) || 0}</span>
            </div>
          </ModalBody>

          <ModalFooter className="px-4 pb-4">
            <Button color="primary" onClickCapture={handleScheduleBulk}>Schedule Message</Button>
            <Button color="secondary" outline onClick={toggleBulkSmsModal}>Cancel</Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

ScheduledMessages.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

const messageColumnStyle = {
  whiteSpace: "normal",
  wordWrap: "break-word",
  maxWidth: "400px",
};

export default ScheduledMessages;
